import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of, tap } from 'rxjs';
import { ENV } from '@envs/environment';
import { type Environment, RequestStatus } from '@enums';
import type { APIResponse } from '@interfaces';
import type { FeatureFlag } from '@appTypes';

/*
  Servicio que obtiene y devuelve feature flags de Trunk-Based
*/

interface FeatureFlagResponseItem {
  proceso: string;
  valor: string;
  dev: boolean;
  qa: boolean;
  prod: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class FeatureFlags {
  // Dependencias
  private readonly http = inject(HttpClient);

  // Atributos
  private requestStatus: RequestStatus = RequestStatus.PENDING;
  private API_URL = 'https://backend10.aws.sicopweb.com/api/10.0.0/global/periodotiempo/trunk-env-config';
  private featureFlags: FeatureFlagResponseItem[] = [];

  // Obtiene y almacena las feature flags
  fetch() {
    if (this.requestStatus === RequestStatus.FULFILLED)
      throw Error('Las feature flags ya están cargadas');

    return this.http.get<APIResponse<{ items: FeatureFlagResponseItem[] }>>(`${ this.API_URL }/list-processes`, {})
      .pipe(tap(res => {
        this.requestStatus = RequestStatus.FULFILLED;
        this.featureFlags = res.response.items;
      }))
      .pipe(catchError(() => {
        this.requestStatus = RequestStatus.REJECTED;
        return of(null);
      } ));
  }

  // Devuelve los datos completos de la feature flag indicada en el ambiente actual
  getData(name: FeatureFlag, fallbacks?: { dev?: boolean; qa?: boolean; prod?: boolean; value?: string; }):
    { found: boolean, value: string, active: boolean, env: Environment} {
    const item = this.featureFlags.find(featureFlag => featureFlag.proceso === name);
    return {
      found: item !== undefined,
      value: item?.valor ?? fallbacks?.value ?? '',
      env: ENV.name,
      active: (ENV.name === 'dev' && (item?.dev ?? fallbacks?.dev ?? false)) ||
              (ENV.name === 'qa' && (item?.qa ?? fallbacks?.qa ?? false)) ||
              (ENV.name === 'prod' && (item?.prod ?? fallbacks?.prod ?? false)),
    };
  }

  // Devuelve verdadero si la feature flag indicada está activa en el ambiente actual
  isActive(name: FeatureFlag, fallbacks?: { dev?: boolean; qa?: boolean; prod?: boolean }): boolean {
    return this.getData(name, fallbacks).active;
  }
}
